import { ChevronDown } from 'lucide-react';
import { useEffect, useState } from 'react';
import { PREFERENCES_CARDS, settingsPanesFor, type PeriodFocus, type SettingsPane, type StaffUser } from '../../nav';
import { useScreenViewed } from '../../api';
import { useRoom } from '../../room';
import { useMainScroll } from '../../scroll';
import { useUnsaved } from '../../unsaved';
import { SalaryModelsPane } from '../fantasy/SalaryModelsPane';
import { PointsSystemsPane } from '../points/PointsSystemsPane';
import { TemplatesPane } from '../templates/TemplatesPane';
import { DefaultTagsPane } from './DefaultTagsPane';
import { PeriodsCard } from './PeriodsCard';
import { PreferencesPane } from './PreferencesPane';
import { RoomPane } from './RoomPane';

/**
 * Everything the room configures about itself, behind one section.
 *
 * The frame is owned here and the panes are only their content: one page heading, one strip of
 * sub-navigation, one set of margins. A pane that brought its own `p-8` and its own `<h1>` would
 * read as a second page nested inside this one — which is exactly what Templates was before it
 * moved under here.
 *
 * The strip mirrors the shell's top navigation deliberately (condensed caps, the current item
 * marked with a rule rather than colour) so "where am I" is answered the same way at both levels.
 * It is a `<nav>` with `aria-current` rather than an ARIA tablist, because these swap the screen
 * the way the sections above them do — the shell already sets that precedent, and a tablist would
 * owe arrow-key navigation and a roving tabindex it doesn't have.
 */
export function SettingsView({
  user,
  pane,
  focus,
  onSelectPane,
}: {
  user: StaffUser;
  /** The pane on screen. `App` holds it, so the Change link beside a period can open Preferences (A6). */
  pane: SettingsPane;
  /** A Change link's request to show the Periods card and focus a field (A6b), or null. */
  focus: PeriodFocus | null;
  onSelectPane: (pane: SettingsPane) => void;
}) {
  const { room } = useRoom();
  const { leave } = useUnsaved();
  useScreenViewed(pane, room?.id ?? null);
  const [panesOpen, setPanesOpen] = useState(false);

  // A pane is a new screen and starts at the top. Templates, Points Systems and Salary Models are
  // much the longest, so leaving one otherwise lands the operator below the whole of a shorter
  // pane — blank space, which reads as an empty section rather than as a scroll position.
  const scrollToTop = useMainScroll();
  useEffect(() => {
    scrollToTop();
  }, [scrollToTop, pane]);

  // The templates, points and salary panes each hold a draft, so a sibling pane is a way out of
  // those editors exactly as the section nav and the room switcher are. Same guard, same question.
  const select = (next: SettingsPane) => leave(() => onSelectPane(next));

  // The shell has already resolved a room by the time anything renders inside it.
  if (!room) return null;

  // The shell greys out a section the platform hasn't switched on for this room rather than
  // hiding it, and this says the same thing the same way — see `SETTINGS_PANES`. Every staff user is
  // offered every pane, Preferences and its Periods card included (UP25, A6): `settingsPanesFor`.
  const panes = settingsPanesFor(user, room);

  return (
    <div className="p-8">
      <header className="mb-6">
        <h1 className="font-display text-2xl font-semibold text-ink-950">Settings</h1>
        <p className="mt-1 max-w-2xl text-sm text-ink-800/60">
          How this room is set up — the templates it fills in, what a finish is worth, what a
          fantasy player costs, the tags new tournaments start with, and what the platform has
          enabled for it — plus the preferences that are yours alone.
        </p>
      </header>

      {/*
       * Two arrangements, the same reasoning as the shell's own navigation one level up. Four
       * entries do not fit 430px, so the strip scrolled sideways and showed about two of them
       * — and it never answered which pane was on screen, which is the question a heading
       * strip is best placed to answer.
       *
       * A disclosure rather than the shell's panel, deliberately. This one expands in place and
       * pushes the pane down instead of overlaying it, so it needs no backdrop and no z-index at
       * all — and the layering `RailPanel` exists to centralise is the thing that would have
       * to be got right if it overlaid. It is also on the chalk ground rather than the charcoal
       * rail, so sharing that component would mean a second palette inside it.
       */}
      <div className="mb-6 md:hidden">
        <button
          type="button"
          onClick={() => setPanesOpen((open) => !open)}
          aria-expanded={panesOpen}
          aria-controls="settings-panes"
          className="flex min-h-[44px] w-full items-center justify-between gap-3 border border-ink-800/15 bg-white px-4 text-left"
        >
          <span className="min-w-0">
            <span className="block text-[10px] font-semibold uppercase tracking-[0.1em] text-ink-800/45">
              Settings section
            </span>
            <span className="block truncate text-[13px] font-semibold uppercase tracking-[0.06em] text-ink-950">
              {panes.find((entry) => entry.pane === pane)?.label}
            </span>
          </span>
          <ChevronDown
            aria-hidden
            className={`size-4 shrink-0 text-ink-800/50 transition ${panesOpen ? 'rotate-180' : ''}`}
          />
        </button>

        {panesOpen ? (
          <nav
            id="settings-panes"
            aria-label="Settings sections"
            className="border border-t-0 border-ink-800/15 bg-white"
          >
            {panes.map((entry) =>
              entry.available ? (
                <button
                  key={entry.pane}
                  type="button"
                  /* Closed before the handler runs: `select` can raise the unsaved-work prompt,
                     and that question has to be read over the pane rather than under a list. */
                  onClick={() => {
                    setPanesOpen(false);
                    select(entry.pane);
                  }}
                  aria-current={pane === entry.pane ? 'page' : undefined}
                  className={`flex min-h-[44px] w-full items-center border-l-[3px] px-4 text-left text-[13px] font-semibold uppercase tracking-[0.06em] ${
                    pane === entry.pane
                      ? 'border-ink-950 text-ink-950'
                      : 'border-transparent text-ink-800/60'
                  }`}
                >
                  {entry.label}
                </button>
              ) : (
                /* The strip above says this with a `title`, which does not exist on touch — so
                   here the reason is a line of its own rather than an unexplained grey word. */
                <span
                  key={entry.pane}
                  className="block border-l-[3px] border-transparent px-4 py-2.5 text-[13px] font-semibold uppercase tracking-[0.06em] text-ink-800/30"
                >
                  {entry.label}
                  <span className="mt-0.5 block text-[11px] font-normal normal-case tracking-normal text-ink-800/45">
                    Ask the platform admin to enable this for your room.
                  </span>
                </span>
              ),
            )}
          </nav>
        ) : null}
      </div>

      <nav
        aria-label="Settings sections"
        className="mb-6 hidden items-stretch overflow-x-auto overflow-y-hidden border-b border-ink-800/10 md:flex"
      >
        {panes.map((entry) =>
          entry.available ? (
            <button
              key={entry.pane}
              type="button"
              onClick={() => select(entry.pane)}
              aria-current={pane === entry.pane ? 'page' : undefined}
              className={`-mb-px flex min-h-[44px] shrink-0 items-center whitespace-nowrap border-b-2 px-4 text-[12px] font-semibold uppercase tracking-[0.08em] transition ${
                pane === entry.pane
                  ? 'border-ink-950 text-ink-950'
                  : 'border-transparent text-ink-800/55 hover:text-ink-950'
              }`}
            >
              {entry.label}
            </button>
          ) : (
            <span
              key={entry.pane}
              title="Ask the platform admin to enable this for your room."
              className="-mb-px flex min-h-[44px] shrink-0 items-center whitespace-nowrap border-b-2 border-transparent px-4 text-[12px] font-semibold uppercase tracking-[0.08em] text-ink-800/25"
            >
              {entry.label}
            </span>
          ),
        )}
      </nav>

      {pane === 'templates' ? (
        <TemplatesPane />
      ) : pane === 'points' ? (
        <PointsSystemsPane />
      ) : pane === 'salary' ? (
        <SalaryModelsPane />
      ) : pane === 'room' ? (
        <RoomPane room={room} />
      ) : (
        // Three cards, not one: default tags are the room's and everybody in it sees them, the
        // preferences below are this operator's on this machine, and Periods are this operator's for
        // this tab only (A6). Composed here rather than nested inside `PreferencesPane` so each stays a
        // card that states its own scope — the pane is named for the second, so the first has to say
        // what it is rather than inherit the name above it. The shared one leads.
        <div className="space-y-6">
          {PREFERENCES_CARDS.map((card) =>
            card === 'defaultTags' ? (
              <DefaultTagsPane key={card} room={room} />
            ) : card === 'yours' ? (
              <PreferencesPane key={card} />
            ) : (
              <PeriodsCard key={card} focus={focus} />
            ),
          )}
        </div>
      )}
    </div>
  );
}
